import type { ComposerReasonCode } from '@/api/models';
import { reportLeak } from '@/lib/telemetry';

/**
 * Why the composer is closed, in words a non-engineer can act on.
 *
 * The server's `CanPrompt(chat, actor)` predicate produces exactly one of these
 * codes, and the same value greys the composer, fills the 409 body and gates
 * the run at start. So the code is never ambiguous - only the wording is ours.
 *
 * The rule the contract states and this file obeys: render a specific string
 * per code. A generic "you can't send this right now" produces a support ticket
 * the operator cannot answer, because they cannot tell which of twenty-three
 * situations the client was in.
 *
 * Three of these are not the client's problem to solve, and saying so is the
 * useful part: a hold, a subscription state and provisioning are all things
 * someone else has to move. Those get `contact` set, so the UI offers the way
 * out instead of leaving the person staring at a disabled box.
 */

export type BlockTone = 'working' | 'waiting' | 'paused' | 'fault' | 'finished';

export interface ReasonCopy {
  /** Short headline for the closed composer. Sentence case, no period. */
  title: string;
  /** One or two sentences. Says what is happening and what happens next. */
  body: string;
  tone: BlockTone;
  /** Offer the "get in touch" affordance - this is not something the client can clear. */
  contact?: boolean;
  /** The composer stays interactive but sending is blocked (nothing today, kept for clarity). */
  transient?: boolean;
}

const REASONS: Record<ComposerReasonCode, ReasonCopy> = {
  ok: {
    title: 'Ready',
    body: 'Describe the change you want.',
    tone: 'finished',
  },
  run_in_progress: {
    title: 'Working on your last request',
    body: 'One change at a time, so nothing gets tangled. You can send the next one as soon as this finishes.',
    tone: 'working',
    transient: true,
  },
  awaiting_answer: {
    title: 'There is a question waiting',
    body: 'Answer the question in the thread above and the work will carry on from there.',
    tone: 'waiting',
  },
  promotion_in_flight: {
    title: 'Your change is going live',
    body: 'Your reviewer is publishing this now. Sending a new request would go out with it half-finished, so the box reopens when publishing is done.',
    tone: 'working',
    transient: true,
  },
  project_frozen: {
    title: 'With your reviewer',
    body: 'This work is being reviewed before it goes live. You can send a new request once the review is finished, or if your reviewer sends it back to you.',
    tone: 'paused',
  },
  pending_review: {
    title: 'Handed to your reviewer',
    body: 'You marked this done, so it is with a reviewer now. Nothing is needed from you.',
    tone: 'paused',
  },
  context_exhausted: {
    title: 'This conversation is full',
    body: 'It has grown too long to add to. Everything you have already asked for is safe and still on your preview. Get in touch and we will start you a fresh conversation that carries on from exactly here.',
    tone: 'paused',
    contact: true,
  },
  usage_limit_reached: {
    title: 'Usage limit reached',
    body: 'This workspace has hit its limit for now. It clears on its own - get in touch if you need it sooner.',
    tone: 'paused',
    contact: true,
  },
  subscription_not_connected: {
    title: 'Workspace not connected yet',
    body: 'This workspace has not been set up to run changes. Your account manager needs to finish connecting it.',
    tone: 'paused',
    contact: true,
  },
  subscription_reauth_required: {
    title: 'Workspace needs reconnecting',
    body: 'The connection behind this workspace expired. Your account manager can reconnect it - nothing you asked for has been lost.',
    tone: 'paused',
    contact: true,
  },
  subscription_held: {
    title: 'Workspace paused',
    body: 'This workspace has been paused. Everything is kept exactly as it is. Get in touch to have it resumed.',
    tone: 'paused',
    contact: true,
  },
  client_held: {
    title: 'Your account is paused',
    body: 'New requests are paused on your account. Nothing has been deleted. Get in touch and we will sort it out.',
    tone: 'paused',
    contact: true,
  },
  project_held: {
    title: 'This product is paused',
    body: 'Requests are paused for this product. Everything is kept exactly as it is. Get in touch and we will sort it out.',
    tone: 'paused',
    contact: true,
  },
  repo_held: {
    title: 'This product is paused',
    body: 'Part of this product is paused, so changes cannot run right now. Get in touch and we will sort it out.',
    tone: 'paused',
    contact: true,
  },
  expert_held: {
    title: 'Review is paused',
    body: 'The reviewer on this work is unavailable. Your account manager is reassigning it.',
    tone: 'paused',
    contact: true,
  },
  provisioning_incomplete: {
    title: 'Still being set up',
    body: 'This product is not fully set up yet, so there is nowhere to put a change. Your account manager is finishing it.',
    tone: 'paused',
    contact: true,
  },
  not_assigned: {
    title: 'No longer yours to change',
    body: 'You do not have access to this product any more. If that is a mistake, get in touch.',
    tone: 'paused',
    contact: true,
  },
  chat_completed: {
    title: 'This change is live',
    body: 'This piece of work is finished and published. Get in touch to start the next one.',
    tone: 'finished',
    contact: true,
  },
  no_successful_deploy: {
    title: 'Nothing to hand over yet',
    body: 'There is no working preview to review. Ask for a change and wait for the preview to update first.',
    tone: 'fault',
  },
  already_pending_review: {
    title: 'Already with your reviewer',
    body: 'This was handed over already. Nothing is needed from you.',
    tone: 'paused',
  },
  // Expert-side codes. They cannot reach a client surface, and the copy exists
  // so an unexpected one degrades into a truthful holding message rather than
  // an unknown-code report.
  not_checked_out: {
    title: 'With your reviewer',
    body: 'This work is being reviewed. Nothing is needed from you.',
    tone: 'paused',
  },
  claimed_by_other: {
    title: 'With your reviewer',
    body: 'This work is being reviewed. Nothing is needed from you.',
    tone: 'paused',
  },
  not_in_review: {
    title: 'Checking where this stands',
    body: 'Give us a moment and refresh. If it keeps happening, get in touch.',
    tone: 'paused',
    contact: true,
  },
};

/**
 * The graceful failure for a code this build has never heard of.
 *
 * It is deliberately honest rather than reassuring: something is blocking the
 * request, we are not going to guess what, and here is the way out. Guessing
 * would be worse - the codes cover holds, billing and review, and picking the
 * wrong one sends the client to the wrong place.
 */
const UNKNOWN_REASON: ReasonCopy = {
  title: 'Requests are paused right now',
  body: 'Something is holding this up and we cannot tell you what from here. Nothing you have asked for has been lost. Get in touch and we will explain.',
  tone: 'paused',
  contact: true,
};

export function reasonCopy(code: ComposerReasonCode | string | undefined | null): ReasonCopy {
  if (!code) return UNKNOWN_REASON;
  const found = REASONS[code as ComposerReasonCode];
  if (found) return found;
  reportLeak({ kind: 'unknown_reason_code', token: String(code).slice(0, 48) });
  return UNKNOWN_REASON;
}

export const ALL_REASON_CODES = Object.keys(REASONS) as ComposerReasonCode[];
